import { generateUID } from "verdi-common/utils/HelperFunctions";
import { SessionHandler } from "../../APICommands/index.js";
import { sleep } from "verdi-common/utils/timeoutUtils";
import { BufferedAction, EventHandler, PermanentlySortedList } from "../../HelperFunctions/index.js";
import type { Zone } from "../Zone.js";
import { RepeatingIrrigationEvent } from "./RepeatingIrrigationEvent.js";

export class Schedule {
    static _permSortedSingleEventList = new PermanentlySortedList(
        (v) =>
            // @ts-ignore
            v.startTime.valueOf(),
        (a) =>
            // @ts-ignore
            !a.deleted,
    );
    /**
     *
     * @type {Schedule[]}
     */
    static allSchedules = [];

    /**
     *
     * @type {IrrigationEventProto[]}
     */
    eventsInScope = [];
    /**
     * start of the scope, the point after which you want to capture events
     * @type {Date}
     */
    startScope = undefined;
    /**
     * end of the scope, the point before which you want to capture events
     * @type {Date}
     */
    endScope = undefined;
    /**
     * returns true if the zone is in the scope of the schedule
     */
    zoneFilter?: Zone | string | ((zone: Zone) => boolean);

    /**
     *
     * @type {EventHandler}
     */
    onEventsAdded = new EventHandler({}, 200);
    /**
     *
     * @type {EventHandler}
     */
    onEventsRemoved = new EventHandler({}, 200);
    /**
     *
     * @type {EventHandler}
     */
    onEventsChanged = new EventHandler({}, 200);
    /**
     * Fires when the events are first loaded and no changes are presently pending.
     * @type {EventHandler}
     */
    onLoad = new EventHandler();

    /**
     * A promise that will be resolved when the component loads
     * @type {Promise<Schedule>}
     */
    loadPromise;
    private onEventRemovedHook: (...es) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private loadQueue: any[];
    private allRepeatingEventsInLoadQueue: RepeatingIrrigationEvent[];
    private onEventUpdatedHook: (...es) => void;
    private uid: string;

    /**
     *
     * @param {Date} startScope
     * @param {Date} endScope
     * @param zoneFilter
     */
    constructor(startScope: Date, endScope: Date, zoneFilter?: string | Zone | ((zone: Zone) => boolean)) {
        this.uid = generateUID("Sched");
        this.startScope = new Date(startScope);
        this.endScope = new Date(endScope);
        this.zoneFilter = zoneFilter;

        this.onEventUpdatedHook = (...es) => {
            const eventsUpdated = [];
            const eventsRemoved = [];
            es.forEach((e) => {
                const updatedEvent = e.updated || e.target;
                if (this.isInScope(updatedEvent)) {
                    eventsUpdated.push(updatedEvent);
                } else {
                    const eventRemoved = updatedEvent;
                    eventsRemoved.push(eventRemoved);
                    //this.eventsInScope.splice(this.eventsInScope.indexOf(eventRemoved), 1);
                }
            });
            this._newEventsToExclude(...eventsRemoved);
            this.onEventsChanged.trigger({
                target: this,
                eventsChanged: eventsUpdated,
            });
            if (eventsRemoved.length > 0) {
                this.onEventsRemoved.trigger({
                    target: this,
                    eventsRemoved: eventsRemoved,
                });
            }
        };
        this.onEventRemovedHook = (...es) => {
            es.forEach((e) => {
                const eventRemoved = e.removed || e.target;
                this._newEventsToExclude(eventRemoved);
            });
        };
        this.loadQueue = [];
        this.allRepeatingEventsInLoadQueue = [];
        allRepeatingIrrigationEvents.forEach((ri) => {
            this.allRepeatingEventsInLoadQueue.push(ri);
            this.loadQueue.push(
                ri
                    .generateEvents(this.startScope, this.endScope, (z) => this.executeZoneFilter(z))
                    .catch((e) => {
                        console.warn(e);
                    }),
            );
        });
        this.loadPromise = this.load().catch((e) => {
            console.warn(e);
        });
    }

    async load() {
        for (let i = 0; i < this.loadQueue.length; i++) {
            await this.loadQueue[i];
        }
        Schedule.allSchedules.push(this);
        this.update();
        await sleep(Math.floor(150));
        while (this._queuedEventsToInclude.length > 0 || this._queuedEventsToExclude.length > 0) {
            await sleep(Math.floor(Math.random() * 50 + 50));
        }
        await this.onLoad.trigger({ target: this });
        return this;
    }

    /**
     * Sorted with index 0 containing the first event
     * @type {SingleIrrigationEvent[]}
     */
    static get masterSingleEventList() {
        return Schedule._permSortedSingleEventList.value;
    }

    /**
     * Returns true or false depending on whether this zone falls within the zone filter of this schedule.
     */
    executeZoneFilter(z: Zone) {
        if (this.zoneFilter) {
            if (this.zoneFilter instanceof Function) {
                return this.zoneFilter(z);
            } else {
                if (typeof this.zoneFilter === "string" || this.zoneFilter instanceof String) {
                    return z.uid === this.zoneFilter || z.dbID === this.zoneFilter || z.label === this.zoneFilter;
                } else return this.zoneFilter === z;
            }
        }
        return true;
    }

    /**
     *
     * @param {IrrigationEventProto} event
     */
    isInScope(event) {
        if (!event) {
            return false;
        }
        if (event.deleted) {
            return false;
        }
        if (!(event.startTime.valueOf() + event.duration > this.startScope.valueOf())) {
            return false;
        }
        if (!(event.startTime.valueOf() < this.endScope.valueOf())) {
            return false;
        }
        if (!this.executeZoneFilter(event.zone)) {
            return false;
        }
        return true;
    }

    _queuedEventsToInclude = [];
    _eventsToIncludeKey = 0;

    _newEventsToInclude(...newEvents) {
        if (newEvents.length > 0) {
            this._queuedEventsToInclude.push(...newEvents);
            this._eventsToIncludeKey++;
            const temp = this._eventsToIncludeKey;
            setTimeout(() => {
                if (temp === this._eventsToIncludeKey) {
                    const newEvents2 = new Set(this._queuedEventsToInclude);
                    const newEventsList = Array.from(newEvents2);
                    this.eventsInScope.push(...newEventsList);
                    this._queuedEventsToInclude = [];
                    newEventsList.forEach((ne) => {
                        ne.onDelete.addListener(this.onEventRemovedHook, this.uid);
                        ne.onChange.addListener(this.onEventUpdatedHook, this.uid);
                    });
                    this.onEventsAdded.trigger({
                        target: this,
                        eventsAdded: newEventsList,
                    });
                }
            }, 200);
        }
    }

    _queuedEventsToExclude = [];
    _eventsToExcludeKey = 0;

    _newEventsToExclude(...removingEvents) {
        if (removingEvents.length > 0) {
            this._queuedEventsToExclude.push(...removingEvents);
            this._eventsToExcludeKey++;
            const temp = this._eventsToExcludeKey;
            setTimeout(() => {
                if (temp === this._eventsToExcludeKey) {
                    const removingEvents = [...this._queuedEventsToExclude];
                    if (removingEvents.length > 0) {
                        this._queuedEventsToExclude = [];
                        removingEvents.forEach((re) => {
                            if (this.eventsInScope.includes(re)) {
                                this.eventsInScope.splice(this.eventsInScope.indexOf(re), 1);
                            }
                            if (re.onDelete) {
                                re.onDelete.removeListener(this.uid);
                            }
                            if (re.onChange) {
                                re.onChange.removeListener(this.uid);
                            }
                        });

                        this.onEventsRemoved.trigger({
                            target: this,
                            eventsRemoved: removingEvents,
                        });
                    }
                }
            }, 200);
        }
    }

    update() {
        const addedEvents = [];
        const removedEvents = [];
        for (let i = 0; i < this.eventsInScope.length; i++) {
            if (!this.isInScope(this.eventsInScope[i])) {
                removedEvents.push(this.eventsInScope.splice(i, 1));
                i--;
            }
        }
        if (Schedule.masterSingleEventList.length > 0) {
            let masterEventListIndex = 0;
            let max = Schedule.masterSingleEventList.length;
            let min = 0;
            if (
                // @ts-ignore
                Schedule.masterSingleEventList[min].startTime.valueOf() >= this.startScope.valueOf()
            ) {
                max = 0;
                min = 0;
            }
            while (max - min > 5) {
                masterEventListIndex = Math.floor((max + min) / 2);
                // @ts-ignore
                const indexValue = Schedule.masterSingleEventList[masterEventListIndex].startTime.valueOf();
                if (indexValue < this.startScope.valueOf() - 1000 * 60 * 60 * 24) {
                    min = masterEventListIndex;
                } else {
                    max = masterEventListIndex;
                }
            }
            masterEventListIndex = min;
            for (let i = masterEventListIndex; i < Schedule.masterSingleEventList.length; i++) {
                const newElement = Schedule.masterSingleEventList[i];
                if (this.isInScope(newElement) && !this.eventsInScope.includes(newElement)) {
                    addedEvents.push(newElement);
                } // @ts-ignore
                if (newElement.startTime.valueOf() > this.endScope.valueOf()) {
                    break;
                }
            }
        }
        this._newEventsToInclude(...addedEvents);
        this._newEventsToExclude(...removedEvents);
    }
}

Schedule._permSortedSingleEventList.onPushFinished.addListener((...es) => {
    const newEvents = [];
    es.forEach((e) => {
        newEvents.push(...e.newValues);
    });
    Schedule.allSchedules.forEach((sched) => {
        const approvedNewEvents = [];
        newEvents.forEach((ne) => {
            if (sched.isInScope(ne)) {
                approvedNewEvents.push(ne);
            }
        });

        sched._newEventsToInclude(...approvedNewEvents);
        sched.update();
    });
});
const allRepeatingIrrigationEvents: RepeatingIrrigationEvent[] = [];
let repeatingEventsToUpdate = new Set();
const fetchEventsAction = new BufferedAction(
    async () => {
        const arrayOfEventsToUpdate = Array.from(repeatingEventsToUpdate);
        repeatingEventsToUpdate = new Set();
        while (arrayOfEventsToUpdate.length > 0) {
            const target = arrayOfEventsToUpdate.pop();
            for (let i = 0; i < Schedule.allSchedules.length; i++) {
                const sched = Schedule.allSchedules[i];
                await target
                    // @ts-expect-error missing type on variable "target" - could be a RepeatingIrrigationEvent?
                    .generateEvents(sched.startScope, sched.endScope, (z) => sched.executeZoneFilter(z))
                    .catch((e) => {
                        console.warn(e);
                    });
            }
        }
    },
    300,
    true,
);
RepeatingIrrigationEvent.onNewRepeatingIrrigationEvent.addListener((...es) => {
    es.forEach((e) => {
        if (e === undefined) {
            return;
        }
        const newRepeatingEvent = e.target;
        allRepeatingIrrigationEvents.push(newRepeatingEvent);
        const fetchEvents = () => {
            repeatingEventsToUpdate.add(newRepeatingEvent);
            fetchEventsAction.trigger();
        };
        newRepeatingEvent.onChange.addListener(fetchEvents);
        newRepeatingEvent.onDelete.addListener(() => {
            if (allRepeatingIrrigationEvents.includes(newRepeatingEvent)) {
                allRepeatingIrrigationEvents.splice(allRepeatingIrrigationEvents.indexOf(newRepeatingEvent), 1);
            }
        });
        fetchEvents();
    });
});

SessionHandler.onSessionUnload.addListener(async () => {
    Schedule._permSortedSingleEventList.clear();
    for (let i = 0; i < Schedule.allSchedules.length; i++) {
        const sched = Schedule.allSchedules[i];
        const eventsInScope = sched.eventsInScope;
        sched.eventsInScope = [];
        await sched.onEventsRemoved.trigger({
            sessionUnload: true,
            target: sched,
            eventsRemoved: eventsInScope,
        });
    }
});
